import { useEffect, useState } from "react";
import { Button, Row, Col } from "rsuite";
import { useTypedDispatch, useTypedSelector } from "App/Store";
import { hideLoader, showLoader } from "redux/action/loaderAction";
import history from "App/History";
import { Brand } from "core/models/Brand";
import { getBrandList } from "redux/action/brandListAction";
import PromptModal from "shared/components/PromptModal";
import { deleteBrand } from "core/services/Brand";
import { useNotification } from "shared/hooks/useNotification";
import { DEFAULT_API_ERROR } from "core/constants/Defaults";
import EditIcon from "@rsuite/icons/legacy/Edit";
import TrashIcon from "@rsuite/icons/legacy/Trash";

export default function Brands() {
  const dispatch = useTypedDispatch();
  const { isLoading, brands } = useTypedSelector((state) => state.brands);
  const [gameFilter, setGameFilter] = useState<"all" | "games">("all");
  const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const showNotification = useNotification();

  useEffect(() => {
    const params: any = {};
    if (gameFilter === "games") params.isGame = true;
    dispatch(getBrandList(params));
  }, [dispatch, gameFilter]);

  useEffect(() => {
    if (isLoading) {
      dispatch(showLoader());
    } else {
      dispatch(hideLoader());
    }
  }, [isLoading, dispatch]);

  function handleOpenDeleteModal(brand: Brand) {
    setSelectedBrand(brand);
    setOpenDeleteModal(true);
  }

  function handleCloseDeleteModal() {
    setOpenDeleteModal(false);
    setTimeout(() => setSelectedBrand(null), 300);
  }

  async function handleDeleteBrand() {
    if (!selectedBrand) return;
    dispatch(showLoader());
    const res = await deleteBrand(selectedBrand.id);
    if (res.status) {
      showNotification({
        message: res.data?.message || "Brand deleted successfully",
        title: "Success",
        type: "success",
      });
      const params: any = {};
      if (gameFilter === "games") params.isGame = true;
      dispatch(getBrandList(params));
    } else {
      showNotification({
        message: res.message || DEFAULT_API_ERROR,
        title: "Error",
        type: "error",
      });
    }
    dispatch(hideLoader());
    handleCloseDeleteModal();
  }

  return (
    <>
      <div className="store-section-bar">
        <div className="store-tabs">
          <div
            className={`tab-item ${gameFilter === "all" ? "active" : ""}`}
            onClick={() => setGameFilter("all")}
          >
            All Brands
          </div>
          <div
            className={`tab-item ${gameFilter === "games" ? "active" : ""}`}
            onClick={() => setGameFilter("games")}
          >
            Games
          </div>
        </div>
        <Button
          appearance="primary"
          onClick={() => history.push("/store/brand/add")}
          className="btn"
        >
          Add Brand
        </Button>
      </div>

      {brands.length ? (
        <Row gutter={20} className="store-brand-grid">
          {brands.map((brand: Brand) => (
            <Col xs={24} sm={12} md={8} lg={6} key={brand.id}>
              <article
                className="store-brand-hover-card"
                onClick={() => history.push(`/store/brand/edit/${brand.id}`)}
              >
                <div
                  className="store-brand-hover-card-cover"
                  style={
                    brand.bannerImage
                      ? {
                          backgroundImage: `url(${brand.bannerImage})`,
                        }
                      : undefined
                  }
                >
                  <div
                    className="store-brand-hover-card-actions"
                    onClick={(event) => event.stopPropagation()}
                  >
                    <div
                      className="store-brand-hover-card-action"
                      onClick={() => history.push(`/store/brand/edit/${brand.id}`)}
                    >
                      <EditIcon />
                    </div>
                    <div
                      className="store-brand-hover-card-action"
                      onClick={() => handleOpenDeleteModal(brand)}
                    >
                      <TrashIcon />
                    </div>
                  </div>

                  <div className="store-brand-hover-card-badges">
                    <span className="store-brand-hover-card-badge">
                      {brand.isGame ? "Game" : "Brand"}
                    </span>
                    {!brand.isPublished && (
                      <span className="store-brand-hover-card-badge is-danger">
                        Unpublished
                      </span>
                    )}
                  </div>
                  <div className="store-brand-hover-card-name">
                    <span className="store-brand-hover-card-title">
                      {brand.name}
                    </span>
                    {brand.developer && (
                      <span className="store-brand-hover-card-subtitle">
                        {brand.developer}
                      </span>
                    )}
                  </div>
                </div>
              </article>
            </Col>
          ))}
        </Row>
      ) : (
        <div className="store-dummy-container">
          <span className="store-dummy-text">No brands added yet</span>
          <span className="store-dummy-content">
            Create a brand (e.g. Valorant, PlayStation Store) to group your
            products.
          </span>
        </div>
      )}

      <PromptModal
        open={openDeleteModal}
        onAccept={handleDeleteBrand}
        onReject={handleCloseDeleteModal}
        acceptText="Delete"
        rejectText="Cancel"
        title="Delete Brand"
        message={`Are you sure you want to delete ${
          selectedBrand?.name || "this brand"
        }?`}
      />
    </>
  );
}
